import { takeLatest, put, call } from "redux-saga/effects";
import i18next from 'i18next';
import { resolveError } from 'utils/resolveError';
import api from 'api/client';
import * as auth from 'reducer/rAuth';
import * as message from "reducer/rMessageBox";

/**
 * 포탈 로그인과 최초 관리자 셋업.
 *
 * <p>TFL 화면에서 이관하며 바뀐 것 셋 —
 * (1) 경로가 `/rest/users/*` 에서 `/api/*` 로,
 * (2) 로그인 성공 판정이 `status === 'success'` 에서 <b>응답 봉투의 `resultCode`</b> 로
 *     (다른 모든 응답과 같은 형태를 쓴다),
 * (3) 셋업이 <b>이메일</b>을 받고 <b>로그인도 이메일로 한다</b>. 계정 아이디는 `admin` 으로 고정돼 있다 —
 *     계정 수를 세는 방식으로는 동시 요청을 막을 수 없어 기본키로 막기 때문이다. 이메일은
 *     초대 메일의 발신자이자 비밀번호 재설정 경로라 실제 주소여야 한다.</p>
 */
export function* sAuth() {
    yield takeLatest(auth.fetchSignInRequest, fetchSignIn);
    yield takeLatest(auth.fetchSignOutRequest, fetchSignOut);
    yield takeLatest(auth.fetchVerifyMfaRequest, fetchVerifyMfa);
    yield takeLatest(auth.fetchResendMfaRequest, fetchResendMfa);
    yield takeLatest(auth.fetchSetupRequiredRequest, fetchSetupRequired);
    yield takeLatest(auth.fetchInitialSetupRequest, fetchInitialSetup);
    yield takeLatest(auth.fetchMeRequest, fetchMe);
}

/**
 * 로그인 실패 코드를 화면에 보여줄 코드로 바꾼다.
 *
 * <p>자격 오류는 이유를 나누지 않는다 - 어떤 아이디가 존재하는지 알려 주는 셈이다.
 * 비밀번호를 맞게 넣고도 막히는 경우만 예외다. 이유를 말해 주지 않으면 비밀번호를 계속
 * 다시 친다.</p>
 * <ul>
 *   <li>계약 만료</li>
 *   <li>인증 코드 메일 발송 실패. 서버는 SMTP 원인별 코드(MAIL_SEND_*)를 주지만 그 문장은
 *       메일 설정 화면용이라 로그인하는 사람이 고칠 수 없다 - 하나로 묶는다</li>
 * </ul>
 */
const signInFailureCode = (resultMessage) => {
    if (resultMessage === 'CONTRACT_GRACE_EXPIRED') return 'CONTRACT_GRACE_EXPIRED';
    if (resultMessage?.startsWith('MAIL_SEND_')) return 'MFA_CODE_SEND_FAILED';
    return 'INVALID_CREDENTIALS';
};

function* fetchSignIn(action) {
    try {
        const response = yield call(() =>
            api.post('/users/signIn', action.payload, {
                headers: {
                    // 스프링 시큐리티의 폼 로그인이 처리한다. JSON 이 아니다.
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            })
        );

        if (response.data?.resultCode !== '0000') {
            const code = signInFailureCode(response.data?.resultMessage);
            const msg = i18next.t(`errors.${code}`);
            yield put(auth.fetchSignInFailure(code));
            yield put(message.fetchMessageBoxRequest({
                isError: true, isSuccess: false, message: msg,
            }));
            return;
        }

        // 2단계가 걸린 계정이다. 비밀번호는 맞았지만 <b>아직 로그인된 것이 아니다</b> -
        // 서버 세션에도 인증이 없다. 그래서 여기서 localStorage 를 건드리지 않는다.
        if (response.data?.data?.mfaRequired) {
            yield put(auth.fetchSignInMfaRequired());
            return;
        }

        window.localStorage.setItem(auth.sessionKey, 'authenticated');
        yield put(auth.fetchSignInSuccess());
        // 첫 화면은 라이선스 목록이다. 대시보드는 이관하지 않았다 — 백엔드가 없다.
        //
        // href 가 아니라 replace 다. href 는 기록을 하나 더 쌓아 `/login` 을 뒤로가기
        // 스택에 남기는데, 그 자리의 문서는 <b>로그인 전에 만들어진 것</b>이다.
        // 뒤로 한 번이면 그 문서가 되살아나 로그인 화면이 다시 보였다.
        window.location.replace('/licenses');
    } catch (error) {
        const msg = error.response?.status === 401
            ? i18next.t('errors.INVALID_CREDENTIALS')
            : i18next.t('common.unexpectedError');
        yield put(auth.fetchSignInFailure(msg));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, message: msg,
        }));
    } finally {
        yield put(auth.setLoading(false));
    }
}

/**
 * 메일로 받은 코드를 확인한다. 여기를 지나야 세션에 인증이 생긴다.
 */
function* fetchVerifyMfa(action) {
    try {
        const response = yield call(() =>
            api.post('/users/signIn/verify', { code: action.payload.code })
        );

        if (response.data?.resultCode !== '0000') {
            const code = response.data?.resultMessage;
            yield put(auth.fetchVerifyMfaFailure(code));
            yield put(message.fetchErrorMessageBoxRequest(resolveError(code)));
            // 더 시도할 수 없는 상태는 화면도 되돌린다. 코드 칸을 남겨 두면 몇 번을 넣어도
            // 같은 오류가 나는데 왜 그런지 화면이 말하지 않는다.
            if (code === 'MFA_ATTEMPTS_EXCEEDED' || code === 'MFA_CODE_EXPIRED'
                    || code === 'MFA_NOT_PENDING') {
                yield put(auth.setMfaRestart());
            }
            return;
        }

        window.localStorage.setItem(auth.sessionKey, 'authenticated');
        yield put(auth.fetchSignInSuccess());
        window.location.replace('/licenses');
    } catch (error) {
        const msg = i18next.t('common.unexpectedError');
        yield put(auth.fetchVerifyMfaFailure(msg));
        yield put(message.fetchErrorMessageBoxRequest(msg));
    } finally {
        yield put(auth.setLoading(false));
    }
}

/** 코드를 다시 보낸다. 옛 코드는 이 순간부터 통하지 않는다. */
function* fetchResendMfa() {
    try {
        const response = yield call(() => api.post('/users/signIn/resend'));
        if (response.data?.resultCode !== '0000') {
            const code = response.data?.resultMessage;
            yield put(message.fetchErrorMessageBoxRequest(code?.startsWith('MAIL_SEND_')
                ? i18next.t('errors.MFA_CODE_SEND_FAILED')
                : resolveError(response.data)));
            return;
        }
        yield put(message.fetchMessageBoxRequest({
            isError: false, isSuccess: true, resultCode: '0000',
            message: i18next.t('auth.mfaResent'),
        }));
    } catch (error) {
        yield put(message.fetchErrorMessageBoxRequest(i18next.t('common.unexpectedError')));
    }
}

/**
 * 로그아웃.
 *
 * <p><b>서버 세션을 먼저 끊는다.</b> 예전에는 localStorage 만 비우고 새로고침했는데,
 * 그러면 쿠키와 서버 세션이 그대로 살아 있었다 — 뒤로가기로 돌아가면 화면이 다시
 * 움직였고, 같은 브라우저를 쓰는 다음 사람에게도 열려 있었다. 세션 쿠키는 HttpOnly 라
 * 스크립트가 지울 수 없다. 지우는 것은 서버뿐이다.</p>
 *
 * <p>호출이 실패해도 화면은 내보낸다. 서버에 닿지 못했다고 로그인 상태로 묶어 두면
 * 나가려는 사람이 나가지 못한다.</p>
 */
function* fetchSignOut() {
    try {
        yield call(() => api.post('/users/signOut'));
    } catch (error) {
        // 삼킨다. 아래에서 어차피 로그인 화면으로 보낸다.
    } finally {
        window.localStorage.removeItem(auth.sessionKey);
        // replace 다. 로그아웃한 화면이 뒤로가기 스택에 남으면 안 된다.
        window.location.replace('/login');
    }
}

/** 계정이 하나도 없으면 셋업 페이지로 보낸다. */
function* fetchSetupRequired() {
    try {
        const response = yield call(() => api.get('/setup/required'));
        if (response.data.resultCode === '0000') {
            yield put(auth.fetchSetupRequiredSuccess(response.data.data?.required));
        } else {
            // 조회 실패 시 false 로 둔다 — 셋업 화면을 잘못 띄우면 이미 있는 관리자가
            // 다시 만들려다 실패하는 것으로 보인다.
            yield put(auth.fetchSetupRequiredFailure(resolveError(response.data)));
        }
    } catch (error) {
        yield put(auth.fetchSetupRequiredFailure(error.message));
    }
}

function* fetchInitialSetup(action) {
    try {
        const { email, password } = action.payload;
        const response = yield call(() => api.post('/setup', { email, password }));

        if (response.data.resultCode === '0000') {
            yield put(auth.fetchInitialSetupSuccess());
        } else {
            const msg = resolveError(response.data)
                || i18next.t('errors.SETUP_FAILED');
            yield put(auth.fetchInitialSetupFailure(msg));
            yield put(message.fetchErrorMessageBoxRequest(msg));
        }
    } catch (error) {
        const msg = resolveError(error.response?.data)
            || i18next.t('errors.SETUP_FAILED');
        yield put(auth.fetchInitialSetupFailure(msg));
        yield put(message.fetchErrorMessageBoxRequest(msg));
    }
}

/**
 * 로그인한 계정을 읽는다.
 *
 * <p>새로고침하면 세션은 살아 있는데 화면 상태는 사라지므로, 로그인 응답에 실어 보내는 것으로는
 * 다시 그릴 수 없다. 그래서 별도 조회로 둔다.</p>
 */
function* fetchMe() {
    try {
        // 브라우저 시간대를 함께 보낸다. 파트너사 시간대가 비어 있으면 서버가 이 값으로 채우고,
        // 만료 알림이 그 현지 아침에 나간다.
        const response = yield call(() => api.get('/users/me', {
            headers: { 'X-Time-Zone': browserTimeZone() },
        }));
        if (response.data?.resultCode === '0000') {
            yield put(auth.fetchMeSuccess(response.data.data));
        } else {
            yield put(auth.fetchMeFailure());
        }
    } catch (error) {
        // 화면은 떠야 한다. 메시지를 띄우지 않는다 - 로그인 직후 401 은 정상 흐름이다.
        yield put(auth.fetchMeFailure());
    }
}

/** 브라우저가 알려주는 시간대(Europe/Berlin 등). 못 읽으면 보내지 않는다. */
function browserTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch (e) {
        return undefined;
    }
}
